/**
 * Engagement Detail — shows engagement metadata + work-item table +
 * engagement-wide thread. Route: /projects/:id
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CaretLeft, Plus, PaperPlaneRight } from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import ThreadPanel from "./components/ThreadPanel";
import WorkspacesPanel from "./components/WorkspacesPanel";
import {
    formatDate, formatUserName, StateBadge, PriorityBadge,
    PageLoading, PageError,
} from "./components/ui";


export default function ProjectDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const perms = new Set(user?.permissions || []);
    const canCreate = perms.has("project:create") || user?.role === "super_admin";
    const canCancelEng = perms.has("project:cancel_engagement") || user?.role === "super_admin";

    const [engagement, setEngagement] = useState(null);
    const [workItems, setWorkItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newWiOpen, setNewWiOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/projects/engagement/${id}`);
            setEngagement(r.data.engagement);
            setWorkItems(r.data.work_items_summary || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (loading) return <PageLoading label="Loading engagement…" testId="op-detail-loading" />;
    if (!engagement) return (
        <PageError
            title="Engagement not found"
            message="This engagement may have been removed or you no longer have access."
            backTo="/projects"
            backLabel="Back to Projects"
            testId="op-detail-not-found"
        />
    );

    return (
        <div className="space-y-6" data-testid="op-engagement-detail-page">
            <PageHeader
                eyebrow="Delivery / Projects"
                title={engagement.name}
                description={
                    <span className="flex items-center gap-2 text-xs">
                        <span className="font-mono text-slate-500">{engagement.engagement_number}</span>
                        <span className="text-slate-300">·</span>
                        <StateBadge state={engagement.status} testId="op-eng-status-badge" />
                    </span>
                }
                actions={
                    <>
                        <Link to="/projects" data-testid="op-back-btn">
                            <Button variant="outline"><CaretLeft className="w-4 h-4 mr-1" /> Back</Button>
                        </Link>
                        {engagement.status === "active" && canCreate && (
                            <Button onClick={() => setNewWiOpen(true)} data-testid="op-add-workitem-btn">
                                <Plus className="w-4 h-4 mr-1" /> Add Work Item
                            </Button>
                        )}
                    </>
                }
            />

            {/* Provenance display-only */}
            {engagement.provenance && (
                <div className="text-xs text-slate-500" data-testid="op-provenance-badge">
                    Originated from{" "}
                    <span className="capitalize">{String(engagement.provenance.source_type || "").replace(/_/g, " ")}</span>
                    {engagement.provenance.source_number && (
                        <>
                            {" · "}
                            <span className="font-mono">{engagement.provenance.source_number}</span>
                        </>
                    )}
                </div>
            )}

            <WorkspacesPanel
                engagementId={engagement.id}
                canManage={canCreate || perms.has("project:assign") || user?.role === "super_admin"}
            />

            <section>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Work Items</h2>
                {workItems.length === 0 ? (
                    <div className="text-sm text-slate-500 italic border border-dashed border-slate-200 rounded-md p-4" data-testid="op-workitems-empty">
                        No work items yet. {canCreate && engagement.status === "active" && "Click \"Add Work Item\" above to get started."}
                    </div>
                ) : (
                    <div className="overflow-x-auto border border-slate-200 rounded-md">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                                <tr>
                                    <th className="text-left px-3 py-2">Number</th>
                                    <th className="text-left px-3 py-2">Title</th>
                                    <th className="text-left px-3 py-2">State</th>
                                    <th className="text-left px-3 py-2">Priority</th>
                                    <th className="text-left px-3 py-2">Target</th>
                                    <th className="text-left px-3 py-2">Primary Owner</th>
                                </tr>
                            </thead>
                            <tbody>
                                {workItems.map((w) => (
                                    <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`op-wi-row-${w.id}`}>
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <Link
                                                data-testid={`op-wi-link-${w.id}`}
                                                to={`/projects/${engagement.id}/w/${w.id}`}
                                                className="text-emerald-600 hover:underline"
                                            >
                                                {w.work_item_number}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-slate-900 max-w-[24rem] truncate">{w.title}</td>
                                        <td className="px-3 py-2">
                                            <StateBadge state={w.state} testId={`op-wi-state-${w.id}`} />
                                        </td>
                                        <td className="px-3 py-2">
                                            <PriorityBadge priority={w.priority} testId={`op-wi-priority-${w.id}`} />
                                        </td>
                                        <td className="px-3 py-2 text-slate-600 text-xs whitespace-nowrap" data-testid={`op-wi-target-${w.id}`}>
                                            {formatDate(w.target_date)}
                                        </td>
                                        <td className="px-3 py-2 text-slate-700 truncate max-w-[12rem]" data-testid={`op-wi-owner-${w.id}`}>
                                            {formatUserName(w.primary_owner_snapshot, w.primary_owner_user_id)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <ThreadPanel
                scope="engagement"
                engagementId={engagement.id}
                workItemId={null}
            />

            {canCancelEng && engagement.status === "active" && (
                <CancelEngagementButton engagement={engagement} onCancelled={load} />
            )}

            <NewWorkItemDialog
                open={newWiOpen}
                engagement={engagement}
                onClose={() => setNewWiOpen(false)}
                onCreated={() => { setNewWiOpen(false); load(); }}
            />
        </div>
    );
}


function CancelEngagementButton({ engagement, onCancelled }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [open, setOpen] = useState(false);
    const submit = async () => {
        if (reason.trim().length < 3) { toast.error("A reason is mandatory."); return; }
        setSubmitting(true);
        try {
            const idempotencyKey = `op-eng-cancel-${engagement.id}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/engagement/${engagement.id}/cancel`,
                { reason: reason.trim() },
                { headers: { "If-Match": String(engagement._version), "Idempotency-Key": idempotencyKey } },
            );
            toast.success("Engagement cancelled.");
            setOpen(false);
            onCancelled?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <div className="pt-4 border-t border-slate-100">
            <Button variant="destructive" size="sm" onClick={() => setOpen(true)} data-testid="op-cancel-engagement-btn">
                Cancel Engagement…
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Cancel this engagement?</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <p className="text-sm text-slate-600">
                            Cancellation is a soft-terminal action. Messages, work items, and the audit trail are preserved. Please share a short reason so future readers understand the context.
                        </p>
                        <Label htmlFor="op-cancel-reason">Reason</Label>
                        <Textarea id="op-cancel-reason" data-testid="op-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Keep engagement</Button>
                        <Button variant="destructive" onClick={submit} disabled={submitting} data-testid="op-cancel-engagement-submit">
                            {submitting ? "Cancelling…" : "Confirm cancellation"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


export function NewWorkItemDialog({ open, engagement, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [nature, setNature] = useState("development");
    // UAT hotfix 2026-07-25 · Work Role master + ad-hoc gate.
    const [workRoles, setWorkRoles] = useState([]);
    const [workRoleId, setWorkRoleId] = useState("");
    const [roleLabel, setRoleLabel] = useState("");
    const [roleSource, setRoleSource] = useState("master");
    const [adHocReason, setAdHocReason] = useState("");
    const [priority, setPriority] = useState("normal");
    const [targetDate, setTargetDate] = useState("");
    const [ownerId, setOwnerId] = useState("");
    const [candidates, setCandidates] = useState([]);
    const [ownerLoading, setOwnerLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    // Capability gate — server enforces too; UI hides the ad-hoc option
    // for users who do not hold the permission.
    const canAdHoc = (() => {
        try {
            const raw = localStorage.getItem("reedos_current_user");
            if (!raw) return false;
            const me = JSON.parse(raw);
            if (me?.role === "super_admin") return true;
            return (me?.permissions || []).includes("work_item:assign_ad_hoc_role");
        } catch { return false; }
    })();

    useEffect(() => {
        if (!open) return;
        // Reset form when dialog opens so subsequent creates don't carry
        // stale text from the previous item.
        setTitle(""); setDescription(""); setRoleLabel("");
        setTargetDate(""); setOwnerId("");
        setNature("development"); setRoleSource("master"); setPriority("normal");
        setWorkRoleId(""); setAdHocReason(""); setCandidates([]);
        (async () => {
            try {
                const wrRes = await api.get("/projects/work-role/list?page_size=200");
                setWorkRoles(wrRes.data?.items || []);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [open, engagement.id]);

    // Owner picker reacts to Work Role / role source changes.
    useEffect(() => {
        if (!open) return;
        // Reset selected owner whenever the eligibility criteria change.
        setOwnerId("");
        setCandidates([]);
        if (roleSource === "master" && !workRoleId) return;
        if (roleSource === "ad_hoc" && !canAdHoc) return;
        setOwnerLoading(true);
        api.get("/projects/_meta/eligible-owners", {
            params: {
                engagement_id: engagement.id,
                role_source: roleSource,
                ...(roleSource === "master" ? { work_role_id: workRoleId } : {}),
                limit: 50,
            },
        })
            .then((r) => setCandidates(r.data.items || []))
            .catch((err) => {
                // Structured backend rejects are surfaced as toasts.
                setCandidates([]);
                if (err?.response?.status === 400 || err?.response?.status === 403) {
                    const d = err.response.data?.detail;
                    if (d?.message) toast.error(d.message);
                    else toast.error(formatApiError(err));
                } else {
                    toast.error(formatApiError(err));
                }
            })
            .finally(() => setOwnerLoading(false));
    }, [open, engagement.id, roleSource, workRoleId, canAdHoc]);

    const submit = async () => {
        const missing = [];
        if (!title.trim()) missing.push("Title");
        if (!description.trim()) missing.push("Description");
        if (roleSource === "master") {
            if (!workRoleId) missing.push("Work Role");
        } else {
            if (!roleLabel.trim()) missing.push("Work Role");
            if (!adHocReason.trim() || adHocReason.trim().length < 10) missing.push("Ad-hoc reason (min 10 chars)");
        }
        if (!ownerId) missing.push("Primary Owner");
        if (missing.length) {
            toast.error(`Required: ${missing.join(", ")}.`);
            return;
        }
        setSaving(true);
        try {
            const key = `op-wi-create-${engagement.id}-${crypto.randomUUID?.() || Date.now()}`;
            await api.post(
                `/projects/engagement/${engagement.id}/work-item`,
                {
                    title: title.trim(),
                    description: description.trim(),
                    nature_of_work: nature,
                    role_source: roleSource,
                    ...(roleSource === "master"
                        ? { work_role_id: workRoleId }
                        : { role_label: roleLabel.trim(), ad_hoc_reason: adHocReason.trim() }),
                    primary_owner_user_id: ownerId,
                    priority,
                    ...(targetDate ? { target_date: targetDate } : {}),
                },
                { headers: { "Idempotency-Key": key } },
            );
            toast.success("Work item created.");
            onCreated?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const wrEmpty = workRoles.length === 0;
    const canSubmit = !saving
        && title.trim().length > 0
        && description.trim().length > 0
        && (roleSource === "master"
            ? workRoleId
            : roleLabel.trim() && adHocReason.trim().length >= 10)
        && ownerId;

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? null : onClose?.())}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="op-new-wi-dialog">
                <DialogHeader><DialogTitle>New Work Item</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                    {/* UAT hotfix — empty-state banner for Work Role master. */}
                    {wrEmpty && (
                        <div
                            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                            data-testid="op-wi-work-role-empty-banner"
                        >
                            No assignable Work Roles are configured. Configure
                            the Work Role Master and employee eligibility
                            before creating a work item.
                        </div>
                    )}
                    <div>
                        <Label htmlFor="op-wi-title">
                            Title <span className="text-rose-600">*</span>
                        </Label>
                        <Input id="op-wi-title" data-testid="op-wi-title" value={title} onChange={(e) => setTitle(e.target.value)} />
                    </div>
                    <div>
                        <Label htmlFor="op-wi-desc">
                            Description <span className="text-rose-600">*</span>
                        </Label>
                        <Textarea id="op-wi-desc" data-testid="op-wi-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>Nature of Work</Label>
                            <Select value={nature} onValueChange={setNature}>
                                <SelectTrigger data-testid="op-wi-nature-trigger"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="development">Development</SelectItem>
                                    <SelectItem value="support">Support</SelectItem>
                                    <SelectItem value="accounts_followup">Accounts / payment follow-up</SelectItem>
                                    <SelectItem value="sales_prospect_followup">Sales / prospect follow-up</SelectItem>
                                    <SelectItem value="other_authorised_category">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Priority</Label>
                            <Select value={priority} onValueChange={setPriority}>
                                <SelectTrigger data-testid="op-wi-priority-trigger"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="normal">Normal</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>
                                Work Role <span className="text-rose-600">*</span>
                            </Label>
                            <Select value={roleSource} onValueChange={(v) => {
                                setRoleSource(v);
                                setWorkRoleId("");
                                setRoleLabel("");
                                setAdHocReason("");
                            }}>
                                <SelectTrigger data-testid="op-wi-rolesrc-trigger"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="master">From Work Role Master</SelectItem>
                                    {canAdHoc && (
                                        <SelectItem value="ad_hoc">Ad-hoc (controlled exception)</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        {roleSource === "master" ? (
                            <div>
                                <Label htmlFor="op-wi-work-role">
                                    Work Role <span className="text-rose-600">*</span>
                                </Label>
                                <Select value={workRoleId} onValueChange={setWorkRoleId} disabled={wrEmpty}>
                                    <SelectTrigger id="op-wi-work-role" data-testid="op-wi-work-role-trigger">
                                        <SelectValue placeholder={wrEmpty ? "Configure Work Role Master first" : "Choose Work Role..."} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {workRoles.map((wr) => (
                                            <SelectItem
                                                key={wr.id}
                                                value={wr.id}
                                                data-testid={`op-wi-work-role-option-${wr.id}`}
                                            >
                                                {wr.name} <span className="text-xs text-slate-500">({wr.code})</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div>
                                <Label htmlFor="op-wi-adhoc-label">
                                    Ad-hoc Work Role Label <span className="text-rose-600">*</span>
                                </Label>
                                <Input id="op-wi-adhoc-label" data-testid="op-wi-adhoc-label" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} placeholder="e.g. Custom Reviewer" />
                            </div>
                        )}
                    </div>
                    {roleSource === "ad_hoc" && (
                        <div>
                            <Label htmlFor="op-wi-adhoc-reason">
                                Ad-hoc reason <span className="text-rose-600">*</span>
                                <span className="text-slate-400 text-xs"> (min 10 chars)</span>
                            </Label>
                            <Textarea
                                id="op-wi-adhoc-reason"
                                data-testid="op-wi-adhoc-reason"
                                value={adHocReason}
                                onChange={(e) => setAdHocReason(e.target.value)}
                                rows={2}
                                placeholder="Why this work item bypasses the Work Role Master."
                            />
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="op-wi-target">Target date <span className="text-slate-400 text-xs">(optional)</span></Label>
                            <Input id="op-wi-target" type="date" data-testid="op-wi-target-date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
                        </div>
                        <div>
                            <Label>
                                Primary Owner <span className="text-rose-600">*</span>
                            </Label>
                            <Select
                                value={ownerId}
                                onValueChange={setOwnerId}
                                disabled={roleSource === "master" ? !workRoleId : !canAdHoc}
                            >
                                <SelectTrigger data-testid="op-wi-owner-trigger">
                                    <SelectValue placeholder={
                                        roleSource === "master" && !workRoleId
                                            ? "Select Work Role first"
                                            : ownerLoading
                                                ? "Loading eligible owners..."
                                                : (candidates.length === 0
                                                    ? "No eligible owners"
                                                    : "Choose Primary Owner...")
                                    } />
                                </SelectTrigger>
                                <SelectContent>
                                    {candidates.map((c) => (
                                        <SelectItem key={c.linked_user_id || c.employee_id} value={c.linked_user_id}>
                                            <div className="flex flex-col text-left">
                                                <span className="font-medium">{c.full_name}</span>
                                                <span className="text-xs text-slate-500">
                                                    {c.employee_code} · {c.designation}{c.department ? ` · ${c.department}` : ""}
                                                </span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="op-wi-submit"
                    >
                        {saving ? "Creating…" : "Create Work Item"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
